import type { ThemeMetadata } from '@/types/theme';

interface ThemeCardProps {
  metadata: ThemeMetadata;
  isSelected: boolean;
  isLive?: boolean;
  onClick: () => void;
}

const COMPLEXITY_LABEL: Record<string, string> = {
  simple: 'Simple',
  medium: 'Medium',
  rich: 'Rich',
};

const COMPLEXITY_COLOR: Record<string, string> = {
  simple: 'bg-emerald-100 text-emerald-700',
  medium: 'bg-amber-100 text-amber-700',
  rich: 'bg-purple-100 text-purple-700',
};

export default function ThemeCard({ metadata, isSelected, isLive = false, onClick }: ThemeCardProps) {
  return (
    <div
      id={`theme-option-${metadata.id}`}
      role="option"
      aria-selected={isSelected}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={`cursor-pointer rounded-lg overflow-hidden border-2 bg-theme-surface transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2 ${
        isSelected
          ? 'border-theme-accent'
          : 'border-theme-border hover:border-theme-accent/50'
      }`}
    >
      {/* Thumbnail with badges */}
      <div className="relative">
        <img
          src={metadata.thumbnail}
          alt={metadata.displayName}
          loading="lazy"
          width={320}
          height={200}
          className="w-full object-cover"
          style={{ height: 200 }}
          onError={(e) => {
            (e.target as HTMLImageElement).style.visibility = 'hidden';
          }}
        />
        {/* Badge row */}
        <div className="absolute top-2 left-2 flex gap-1.5 flex-wrap">
          {isLive && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-600 text-white uppercase tracking-wide">
              Live
            </span>
          )}
          {metadata.featured && (
            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-theme-accent text-white uppercase tracking-wide">
              Featured
            </span>
          )}
        </div>
      </div>

      <div className="p-3">
        <p className="font-semibold text-sm text-theme-text-primary">{metadata.displayName}</p>
        <p className="text-xs mt-1 text-theme-text-secondary line-clamp-2">{metadata.description}</p>
        {/* Complexity badge */}
        <span
          className={`inline-block mt-2 px-2 py-0.5 text-[10px] font-medium rounded-full ${COMPLEXITY_COLOR[metadata.complexity] ?? 'bg-gray-100 text-gray-600'}`}
        >
          {COMPLEXITY_LABEL[metadata.complexity] ?? metadata.complexity}
        </span>
      </div>
    </div>
  );
}
